import type { FC, PropsWithChildren, ReactNode } from "react";
import { useEffect, useRef, useState } from "react";

import { startsWith } from "lodash";
import styled from "styled-components";

import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes, rgba, setFontVariant } from "@similarweb/styles";

import { copySite, decodeSite, refreshTooltip } from "app/components/React/Table/Utils/urlCellsUtils";
import { PlainTooltip } from "app/components/React/Tooltip/PlainTooltip/PlainTooltip";
import { WaSearchUrlTooltip } from "app/components/React/Tooltip/WaSearchUrlTooltip/WaSearchUrlTooltip";
import { FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";

const DotsIcon = styled.span`
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding-left: 6px;
`;

const UrlCellAnchor = styled.a`
    visibility: hidden;
`;

export const UrlCellContainer = styled.div`
    width: calc(100% - 50px);
    height: 100%;
    position: absolute;
    top: 0;
    margin: auto 0;
    display: flex;
    align-items: center;
    &:hover {
        ${UrlCellAnchor} {
            visibility: visible;
        }
    }
`;
/*
    Webkit is supported only in Safari, Google Chrome and Konqueror,
    The product knows that this cell will behave differently in other browsers.
 */
const Span = styled.span`
    height: 32px;
    /* stylelint-disable-next-line */
    display: flex;
    align-items: center;
    align-content: center;
    ${setFontVariant("listItem3", rgba(colorsPalettes.carbon[500], 0.8))};
    line-height: 16px;
    word-break: break-all;
    white-space: initial;
    letter-spacing: 0.5px;
    /*
        Three lines below are equivalent to "line-clamp: 2"
        Support for line-clamp in Safari began on Dec 2024
        We should remove these styles when this Cell will be migrated to pro-logic
     */
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
`;

const Icon = styled(SWReactIcons)`
    path {
        fill: ${colorsPalettes.carbon[200]};
    }
`;

export const UrlCellWithTooltipContainer = styled.div`
    display: flex;
    justify-content: space-between;
    width: 100%;
`;

export interface IUrlCellProps {
    site: string;
    displayedSite?: ReactNode;
    tooltipData?: { Key: string; Value: string }[];
    customContainer?: FC<PropsWithChildren>;
    showTooltip?: boolean;
}

export const UrlCell: FC<IUrlCellProps> = (props) => {
    const { showTooltip = null, customContainer: CustomContainer, site, displayedSite, tooltipData } = props;
    const [isTooltipEnabled, setIsTooltipEnabled] = useState<boolean>(false);
    const textContainerRef = useRef<HTMLElement>(null);

    useEffect(() => {
        return refreshTooltip(textContainerRef, setIsTooltipEnabled);
    }, []);

    const siteDecoded = decodeSite(site);

    if (siteDecoded === "N/A" || !siteDecoded) {
        return (
            <UrlCellContainer>
                <Span>-</Span>
            </UrlCellContainer>
        );
    }

    const cellContent = (
        <UrlCellContainer onCopy={(event) => copySite(event, siteDecoded)}>
            <Span
                className="url-cell-content"
                ref={textContainerRef}
            >
                {displayedSite ?? siteDecoded}&nbsp;
            </Span>
            <UrlCellAnchor
                href={startsWith(siteDecoded, "http") ? siteDecoded : `http://${siteDecoded}`}
                target="_blank"
            >
                <Icon
                    iconName="link-out"
                    size="xs"
                />
            </UrlCellAnchor>
        </UrlCellContainer>
    );

    if (CustomContainer) {
        return <CustomContainer>{cellContent}</CustomContainer>;
    }

    if (props.tooltipData) {
        return (
            <UrlCellWithTooltipContainer>
                <FlexRow>{cellContent}</FlexRow>
                <WaSearchUrlTooltip data={tooltipData}>
                    <DotsIcon className="sw-icon-show-more" />
                </WaSearchUrlTooltip>
            </UrlCellWithTooltipContainer>
        );
    }

    return (
        <FlexRow>
            <PlainTooltip
                text={displayedSite ?? siteDecoded}
                cssClassContent="plainTooltip-content-without-space"
                enabled={showTooltip ?? isTooltipEnabled}
            >
                {cellContent}
            </PlainTooltip>
        </FlexRow>
    );
};
